import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PanelEmptyState from '../../Components/PanelEmptyState';
import { getMetaResults } from '../../services/metaAdsService';
import { useMetaRead } from '../hooks/useMetaRead';
import { formatMetaMoney } from '../metaAdsMoney';
import { formatMetaCount, metaPlural } from '../metaFormat';
import { readMetaResults } from '../metaResults';
import { MetaBlockState, MetaCard, MetaFootnote, MetaStatRows } from './MetaDataParts';
import MetaCsvDownloads from './MetaCsvDownloads';
import { useMetaDialogHeaderSlot } from './MetaSubviewDialog';
import MetaSpaceAudience from './MetaSpaceAudience';
import { MetaReportSkeleton } from './MetaSkeletons';
import UnitReportDialog from './UnitReportDialog';
import { metaAdsBodyTextSx, metaAdsMutedTextSx, metaReportUnitActionSx } from './metaAdsUi.styles';

const money = (v, c) => formatMetaMoney(v, c);
const unitName = (u) => u.label || 'Propiedad sin nombre';

/** Resumen del espacio: con monedas mezcladas, la plata va separada por moneda (nunca se suma). */
export function spaceSummaryRows(totals) {
  const t = totals.total || {};
  const rows = [];
  if (totals.mixed) {
    totals.byCurrency.forEach((r) => {
      rows.push({ key: `spend-${r.currency}`, label: `Gasto en ${r.currency}`, value: money(r.metrics.spend, r.currency) });
    });
  } else {
    rows.push({ key: 'spend', label: 'Gasto', value: money(t.spend, totals.currency) });
  }
  rows.push({ key: 'contacts', label: 'Contactos', value: formatMetaCount(t.contacts) });
  if (!totals.mixed) rows.push({ key: 'cpc', label: 'Costo por contacto', value: money(t.cost_per_contact, totals.currency) });
  rows.push({ key: 'impressions', label: 'Impresiones', value: formatMetaCount(t.impressions) });
  if (t.link_clicks != null) rows.push({ key: 'link-clicks', label: 'Clics en el enlace', value: formatMetaCount(t.link_clicks) });
  if (t.ads_count != null) rows.push({ key: 'ads', label: 'Anuncios', value: formatMetaCount(t.ads_count) });
  if (t.units_count != null) rows.push({ key: 'units', label: 'Propiedades con anuncios', value: formatMetaCount(t.units_count) });
  return rows;
}

/** Gasto, contactos y costo por contacto de una propiedad (con monedas mezcladas, un gasto por moneda). */
function unitMetrics(u) {
  const spend = u.mixedCurrencies
    ? (u.byCurrency.length ? u.byCurrency.map((r) => money(r.metrics.spend, r.currency)) : ['—'])
    : [money(u.spend, u.currency)];
  const metrics = [
    { key: 'spend', label: 'Gasto', values: spend },
    { key: 'contacts', label: 'Contactos', values: [formatMetaCount(u.contacts)] },
  ];
  if (!u.mixedCurrencies) metrics.push({ key: 'cpc', label: 'Costo por contacto', values: [money(u.costPerContact, u.currency)] });
  return metrics;
}

const metricLabelSx = { ...metaAdsMutedTextSx, fontSize: '11px', lineHeight: '16px' };
const metricValueSx = { ...metaAdsBodyTextSx, fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' };
const REPORT_UNIT_ACTION = 'Ver informe';

function UnitRow({ unit, onOpen }) {
  const name = unitName(unit);
  return (
    <Box
      component="li"
      data-testid={`meta-report-unit-${unit.id}`}
      sx={{ listStyle: 'none', borderTop: '1px solid rgba(255,255,255,0.07)', pt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...metaAdsBodyTextSx, fontWeight: 600, overflowWrap: 'anywhere' }}>{name}</Typography>
          <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px', lineHeight: '16px' }}>
            {metaPlural(unit.adsCount, 'anuncio', 'anuncios')}
            {unit.sharedAdsCount > 0 ? (
              <Box component="span" data-testid={`meta-report-unit-${unit.id}-shared`}>
                {` · ${metaPlural(unit.sharedAdsCount, 'anuncio compartido', 'anuncios compartidos')} con otras propiedades`}
              </Box>
            ) : null}
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => onOpen(unit)}
          data-testid={`meta-report-unit-${unit.id}-open`}
          sx={metaReportUnitActionSx}
          aria-label={`${REPORT_UNIT_ACTION}: ${name}`}
        >
          {REPORT_UNIT_ACTION}
        </Button>
      </Box>
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', columnGap: 1.5 }}>
        {unitMetrics(unit).map((m) => (
          <Box key={m.key} sx={{ minWidth: 0 }}>
            <Typography component="dt" sx={metricLabelSx}>{m.label}</Typography>
            {m.values.map((v) => <Typography component="dd" key={v} sx={{ ...metricValueSx, m: 0 }}>{v}</Typography>)}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Pestaña Informe: resumen de todo el espacio en el período y una fila por propiedad con
 * «Ver informe» (diálogo con vista previa, PDF y links). Los datos salen de
 * `getmetaresults` (scope=space).
 */
export default function MetaReportContent({ period, ownerUserId = null, includeInactive = false, periodPreset, afterSummary = null }) {
  const headerSlot = useMetaDialogHeaderSlot();
  const read = useMetaRead(
    (signal) => getMetaResults({ period, includeInactive, ownerUserId, signal }),
    { deps: [period.since, period.until, ownerUserId, includeInactive] },
  );
  const [open, setOpen] = useState(null);
  const results = useMemo(() => (read.status === 'ready' ? readMetaResults(read.res) : null), [read.status, read.res]);

  // Resumen y «Por propiedad» dependen de `getmetaresults`; Público y Descargar tienen sus propios pedidos.
  const renderResults = () => {
    if (read.status === 'loading' || read.status === 'idle') return <MetaReportSkeleton />;
    if (read.status !== 'ready') {
      return (
        <MetaCard title="Resumen" testId="meta-report-summary">
          <MetaBlockState
            status={read.status}
            error={read.error}
            onRetry={read.reload}
            testId="meta-report-state"
            unavailableText="El informe todavía no está disponible en este entorno."
          />
        </MetaCard>
      );
    }
    if (!results || results.units.length === 0) {
      return (
        <PanelEmptyState
          icon={DescriptionOutlinedIcon}
          title="Todavía no hay propiedades con anuncios"
          hint="Cuando vincules anuncios a una propiedad, vas a poder armar su informe desde acá."
          sx={{ minHeight: 160 }}
          testId="meta-report-empty"
        />
      );
    }
    return (
      <>
        <MetaCard title="Resumen del espacio" testId="meta-report-summary">
          <MetaStatRows rows={spaceSummaryRows(results.totals)} testId="meta-report-summary-rows" />
          <MetaFootnote testId="meta-report-summary-note">
            Un anuncio compartido entre varias propiedades se cuenta una sola vez en este resumen.
          </MetaFootnote>
        </MetaCard>
        {afterSummary}
        <MetaCard title="Por propiedad" testId="meta-report-by-unit">
          <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }} data-testid="meta-report-units">
            {results.units.map((u) => <UnitRow key={`${u.itemKind}:${u.id}`} unit={u} onOpen={setOpen} />)}
          </Box>
          <MetaFootnote testId="meta-report-units-note">
            Cada propiedad muestra el total de sus anuncios: si comparte uno con otra, el gasto aparece completo en las dos, así que las filas no se suman.
          </MetaFootnote>
        </MetaCard>
      </>
    );
  };

  // «Descargar» (t990u): ⋮ en el header del modal (portal al lugar de `MetaSubviewDialog`); fuera
  // del modal, arriba a la derecha del contenido. Sin el endpoint (no disponible), no va.
  const downloads = read.status === 'unavailable' ? null : (
    <MetaCsvDownloads period={period} ownerUserId={ownerUserId} includeInactive={includeInactive} />
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="meta-report-content">
      {downloads && !headerSlot ? <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>{downloads}</Box> : null}
      {renderResults()}
      {/* Paso 2: el resumen del presupuesto va debajo del Resumen del espacio; si getmetaresults no
          está listo (cargando, no disponible, error, sin propiedades) igual se muestra acá, una sola vez. */}
      {read.status === 'ready' && results && results.units.length > 0 ? null : afterSummary}
      <MetaSpaceAudience period={period} ownerUserId={ownerUserId} />
      {downloads && headerSlot ? createPortal(downloads, headerSlot) : null}
      {open ? (
        <UnitReportDialog
          itemKind={open.itemKind}
          recordId={open.id}
          unitLabel={open.label}
          ownerUserId={ownerUserId}
          initialPreset={periodPreset}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </Box>
  );
}
